import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { config, Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'
import { escapeHtml, unescapeHtml } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'lvc-ticketing-services-field', {
  title: 'Services de billetterie',
  placeholder: 'Rechercher un service de billetterie',
  choose: 'Choisir',
  add: 'Ajouter',
  other: 'Autre',
})

Locale.getInstance().registerKeySet('en', 'lvc-ticketing-services-field', {
  title: 'Ticketing services',
  placeholder: 'Search for a ticketing service',
  choose: 'Choose',
  add: 'Add',
  other: 'Other',
})

export interface LvcTicketingServicesField {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
}

export class LvcTicketingServicesField extends MinzeElement {
  private ticketingServices: any[] = []
  private selectedIds: string[] = []
  private filter: string = ''
  attrs: Attrs = [
    'title',
    ['chooseLabel', Locale.getKey('lvc-ticketing-services-field.choose')],
    'placeholder',
    ['default-value', ''],
    ['on-change', ''],
    ['background', 'transparent'],
    ['border', ''],
    ['dropdownBorder', ''],
    ['error', false],
  ]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'default-value', 'error']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  async onReady() {
    // Data is hardcoded in the config files
    this.ticketingServices = config.ticketingServices

    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  async updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.selectedIds = JSON.parse(decodeURIComponent(this.defaultValue)).map(
        (ticketingService: any) => `${ticketingService.id}`
      )
      this.value = this.defaultValue
    }
    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onTicketingServiceClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (value === 'other') {
      const otherInput = this.select('[name="other"]') as HTMLInputElement

      if (otherInput) {
        const otherValue = unescapeHtml(otherInput.value.trim())

        if (otherValue) {
          // Generate an ID from the entered value, we remove accents, special characters and make the id camel case
          // with "custom-" before the id
          let id = `custom-${otherValue
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`

          // if the item already exists, we don't add it
          if (
            this.ticketingServices.find(
              ticketingService =>
                ticketingService.id === id &&
                (ticketingService.labelFr.toLowerCase() === otherValue.toLowerCase() ||
                  ticketingService.labelEn.toLowerCase() === otherValue.toLowerCase())
            )
          ) {
            return
          }

          // push the new ticketing service to the list but before the last element (other)
          let otherValueLabel = otherValue.charAt(0).toUpperCase() + otherValue.slice(1)
          this.ticketingServices.splice(this.ticketingServices.length - 1, 0, {
            id,
            name: { fr: otherValueLabel, en: otherValueLabel },
          })

          this.selectedIds.push(id)
        }
      }
    } else if (value === 'noTicketingService') {
      // Clicked on "noTicketingService", deselect all other options
      this.selectedIds = [value]
    } else {
      let index = this.selectedIds.indexOf(value as string)

      if (index > -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(value as string)
      }
      // Check if "noTicketingService" is selected and deselect it
      let noTicketingServiceIndex = this.selectedIds.indexOf('noTicketingService')
      if (noTicketingServiceIndex > -1) {
        this.selectedIds.splice(noTicketingServiceIndex, 1)
      }
    }

    this.serializeValue()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let objects = this.ticketingServices.filter(
      ticketingService => this.selectedIds.indexOf(ticketingService.id) > -1
    )
    this.value = encodeURIComponent(JSON.stringify(objects))
    Callback.call(this.onChange, objects)
    this.cast('change', objects)
  }

  getTicketingServicesLabel() {
    if (this.selectedIds.length === 0 && !this.chooseLabel) {
      return Locale.getKey('lvc-ticketing-services-field.choose')
    } else if (this.selectedIds.length === 0 && this.chooseLabel) {
      return this.chooseLabel
    }

    let labels = this.ticketingServices
      .filter(ticketingService => this.selectedIds.indexOf(`${ticketingService.id}`) > -1)
      .map(ticketingService => ticketingService.name[Locale.language])

    if (labels.length === 1) {
      return labels[0]
    }

    if (labels.length > 1) {
      return labels[0] + ' + ' + (labels.length - 1)
    }
  }

  renderTicketingService = (ticketingService: any) => {
    if (
      this.filter &&
      ticketingService.name[Locale.language].toLowerCase().indexOf(this.filter.toLowerCase()) === -1
    ) {
      return ''
    }

    let isNotSelected = this.selectedIds.indexOf(`${ticketingService.id}`) == -1

    if (ticketingService.id === 'other') {
      return `
        <lvc-box display="flex" alignItems="center">
          <lvc-input name="other" padding="8px 8px" placeholder="${Locale.getKey('lvc-ticketing-services-field.other')}" value="${escapeHtml(ticketingService.name)}" on-change="serializeValue" background="transparent"></lvc-input>
          <lvc-button enable-hover-box-shadow="true" name="ticketingService" padding="10px 20px 10px 20px" height="fit-content" data-id="${ticketingService.id}">${Locale.getKey('lvc-ticketing-services-field.add')}</lvc-button>
        </lvc-box>
      `
    }

    return `
      <button 
        name="ticketingService"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${ticketingService.id}">
        ${escapeHtml(ticketingService.name[Locale.language])}
        ${isNotSelected ? '' : `<lvc-circle-check-select-icon data-id="${ticketingService.id}"></lvc-circle-check-select-icon>`}
      </button>
    `
  }

  renderTicketingServices = () => {
    let html = ''

    for (let i = 0; i < this.ticketingServices.length; i++) {
      html += this.renderTicketingService(this.ticketingServices[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    return `
      <div class="title">${this.title || Locale.getKey('lvc-ticketing-services-field.title')}</div>
      <div class="input">
        <button class="dropbtn" name="toggle">
          ${this.getTicketingServicesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <input name="filter" type="text" value="${this.filter}" placeholder="${this.placeholder || Locale.getKey('lvc-ticketing-services-field.placeholder')}">
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="ticketingServices" data-selected="${this.selectedIds.length}">
            ${this.renderTicketingServices()}
          </div>
        </div> 
      </div>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return `
      :host {
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        font-family: inherit;
        color: #1E007D;
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        margin-bottom: 8px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        background: ${this.background ? this.background : 'transparent'};
        position: relative;
        border: ${this.border ? this.border : '1px solid var(--Minuit, #1E007D);'}; 
        border-radius: 24px;
      }

      .ticketingServices {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .ticketingServices button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .ticketingServices button:last-child {
        border-bottom: none;
      }

      .ticketingServices button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 16px;
        font-size: 16px;
        font-weight: 500;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: 100%;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: ${this.dropdownBorder ? this.dropdownBorder : '1px solid var(--Minuit, #1E007D);'}; 
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="ticketingService"]', 'click', this.onTicketingServiceClick.bind(this)],
    ['lvc-button[name="ticketingService"]', 'click', this.onTicketingServiceClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
